import { BREAKPOINTS, ROLE_MAP, GAP, PAD } from './tokens.js';

/**
 * Parse a responsive prop string into a breakpoint → value map.
 *
 * "1"                          → { default: '1' }
 * "1 sm:2 lg:4"                → { default: '1', sm: '2', lg: '4' }
 * "column md:row"              → { default: 'column', md: 'row' }
 * "200px 1fr"                  → { default: '200px 1fr' }   (no breakpoints → kept as-is)
 * "repeat(auto-fill,minmax(…))"→ { default: 'repeat(…)' }  (same)
 *
 * When no breakpoint prefix (sm|md|lg|xl) is present, the entire string is
 * treated as a plain CSS value so multi-word values like "200px 1fr" or
 * "1fr 2fr 1fr" are preserved intact.
 */
export function parseProp(value: string | null | undefined): Record<string, string> {
  if (!value) return {};
  const str = String(value).trim();

  // Fast path: no breakpoint prefix present → whole string is the default value.
  if (!/(?:^|\s)(?:sm|md|lg|xl):/.test(str)) return { default: str };

  // Responsive syntax: group consecutive non-breakpoint tokens as the default value.
  const result: Record<string, string> = {};
  const defaultParts: string[] = [];

  for (const t of str.split(/\s+/).filter(Boolean)) {
    const m = /^(sm|md|lg|xl):(.+)$/.exec(t);
    if (m) {
      if (defaultParts.length) { result.default = defaultParts.join(' '); defaultParts.length = 0; }
      result[m[1]] = m[2];
    } else {
      defaultParts.push(t);
    }
  }
  if (defaultParts.length) result.default = defaultParts.join(' ');

  return result;
}

/**
 * Set ARIA role on the element based on the `as` attribute.
 * Removes the role attribute if `as` is empty or unrecognised.
 */
export function applyRole(el: HTMLElement, as_: string | null | undefined): void {
  const role = as_ ? ROLE_MAP[as_] : null;
  if (role) el.setAttribute('role', role);
  else el.removeAttribute('role');
}

/**
 * Write CSS custom properties for every breakpoint tier, filling forward from
 * the last seen value. This prevents CSS inheritance from leaking a parent
 * component's breakpoint variable into a nested layout component.
 *
 * Example with "column md:row" → prefix = "--_dir":
 *   --_dir: column
 *   --_dir-sm: column   ← filled forward
 *   --_dir-md: row
 *   --_dir-lg: row      ← filled forward
 *   --_dir-xl: row      ← filled forward
 */
export function applyResponsiveProp(
  el: HTMLElement,
  parsed: Record<string, string>,
  prefix: string,
  transform: (v: string) => string = v => v,
): void {
  let last: string | null = null;
  for (const bp of BREAKPOINTS) {
    if (parsed[bp] !== undefined) last = parsed[bp];
    if (last !== null) {
      const prop = bp === 'default' ? prefix : `${prefix}-${bp}`;
      el.style.setProperty(prop, transform(last));
    }
  }
}

/** Resolve a gap token (md → 1rem) or pass through any CSS value. */
export function resolveGap(v: string | null | undefined): string {
  return v ? (GAP[v] ?? v) : '0';
}

/** Resolve a padding token (md → 1rem) or pass through any CSS value. */
export function resolvePad(v: string | null | undefined): string {
  return v ? (PAD[v] ?? v) : '0';
}

/**
 * Convert a numeric column count to a CSS repeat() expression.
 * "3" → "repeat(3,1fr)". CSS values like "200px 1fr" pass through unchanged.
 */
export function resolveCols(v: string): string {
  return /^\d+$/.test(v.trim()) ? `repeat(${v.trim()},1fr)` : v;
}
